import dayjs from "dayjs";
import { uniq, isEqual, sum, isNumber, sortBy, max, min, isBoolean } from "lodash";

import { AggregateFunctions } from "../types/form-type";

const notEmpty = (data: any) => !getIsEmpty(data);

const getIsEmpty = (value: any): boolean => {
  if (Array.isArray(value)) {
    if (isEqual(value, [])) return true;

    if (isBoolean(value[0])) {
      return value.some((item) => item) ? false : true;
    }

    return value.some((v: any) => {
      if (!v && v !== 0) return true;
      if (typeof v !== "object") return false;
      return getIsEmpty(v.fileId) && getIsEmpty(v.visibleName);
    });
  }
  return value === null || typeof value === "undefined" || value === "";
};

const aggCount = (values: any[]) => values.length;

const aggEmpty = (values: any[]) => values.filter(getIsEmpty).length;

const aggFilled = (values: any[]) => values.length - values.filter(getIsEmpty).length;

const aggUnique = (values: any[]) => uniq(values).length;

const aggPercentageEmpty = (values: any[]) => {
  if (values.length === 0) return 100;
  let empty = values.filter(getIsEmpty).length;
  let count = values.length;
  return ((empty / count) * 100).toFixed(1);
};

const aggPercentageFilled = (values: any[]) => {
  if (values.length === 0) return 0;
  let empty = values.filter(notEmpty).length;
  let count = values.length;
  return ((empty / count) * 100).toFixed(1);
};

const aggPercentageUnqiue = (values: any[]) => {
  if (values.length === 0) return 0;
  const filterNotEmpty = values;
  const filterNotEmptyCount = filterNotEmpty.length;
  if (!filterNotEmptyCount) return 0;

  const percentageUnqiueValue = ((uniq(filterNotEmpty).length / values.length) * 100).toFixed(1);
  return percentageUnqiueValue;
};

const aggSum = (values: any[]) => sum(values.filter(isNumber));

const aggAverage = (values: any[]) => {
  if (values.length === 0) return 0;
  const numberList = values.filter(isNumber);
  const numberSum = sum(numberList);
  return Number((numberSum / numberList.length).toFixed(12));
};

const aggMedian = (values: any[]) => {
  const numberList = sortBy(values.filter(isNumber));
  const numberCount = numberList.length;
  if (numberCount === 0) return "";
  const median =
    numberCount % 2 === 0
      ? (numberList[numberCount / 2 - 1] + numberList[numberCount / 2]) / 2
      : numberList[(numberCount - 1) / 2];
  return median;
};

const aggMax = (values: any[]) => {
  const result = max(values.filter(isNumber));
  return isNumber(result) ? result : "";
};

const aggMin = (values: any[]) => {
  const result = min(values.filter(isNumber));
  return isNumber(result) ? result : "";
};

const aggRange = (values: any[]) => {
  const aggMaxNumber = aggMax(values);
  const aggMinNumber = aggMin(values);
  if (!isNumber(aggMaxNumber) || !isNumber(aggMinNumber)) return "";
  return aggMaxNumber - aggMinNumber;
};

const aggStandardDeviation = (values: any[]) => {
  const sum = aggSum(values);
  const count = aggCount(values);
  const squareDiffs = values.filter(isNumber).map((data) => {
    const diff = data - sum / count;
    const sqrDiff = diff * diff;
    return sqrDiff;
  });
  const squareSum = aggSum(squareDiffs);
  const squareCount = aggCount(squareDiffs);

  const standardDeviationValue = Number(Math.sqrt(squareSum / squareCount).toFixed(2));
  return standardDeviationValue;
};

const aggChecked = (values: any[]) => {
  return values.filter(isBoolean).filter((item) => item).length;
};

const aggUnchecked = (values: any[]) => {
  return values.filter(isBoolean).filter((item) => !item).length;
};

const aggCheckedPercentage = (values: any[]) => {
  if (values.length === 0) return 0;
  const count = aggChecked(values);
  const checkedPercentageValue = ((count / values.length) * 100).toFixed(1);
  return checkedPercentageValue;
};

const aggUncheckedPercentage = (values: any[]) => {
  if (values.length === 0) return 0;
  const count = aggUnchecked(values);
  const checkedPercentageValue = ((count / values.length) * 100).toFixed(1);
  return checkedPercentageValue;
};

const aggEarliestDate = (values: any[]) => {
  const list = values.filter(notEmpty);
  if (list.length === 0) return "";
  list.sort((a, b) => Date.parse(a) - Date.parse(b));
  return list[0];
};

const aggLatestDate = (values: any[]) => {
  const list = values.filter(notEmpty);
  if (list.length === 0) return "";
  list.sort((a, b) => Date.parse(b) - Date.parse(a));
  return list[0];
};

const aggDateRangeDays = (values: any[]) => {
  if (values.length === 0) return 0;
  const dateRangeDaysValue = dayjs(aggLatestDate(values)).diff(aggEarliestDate(values), "day");
  return dateRangeDaysValue;
};

const aggDateRangeMonths = (values: any[]) => {
  if (values.length === 0) return 0;
  const dateRangeMonthsValue = dayjs(aggLatestDate(values)).diff(aggEarliestDate(values), "month");
  return dateRangeMonthsValue;
};

const GET_SUMMARY_VALUE_FUNC: { [key in AggregateFunctions]: (values: any[]) => number | string } = {
  [AggregateFunctions.COUNT]: aggCount,
  [AggregateFunctions.EMPTY]: aggEmpty,
  [AggregateFunctions.FILLED]: aggFilled,
  [AggregateFunctions.UNIQUE]: aggUnique,
  [AggregateFunctions.PERCENTAGE_EMPTY]: aggPercentageEmpty,
  [AggregateFunctions.PERCENTAGE_FILLED]: aggPercentageFilled,
  [AggregateFunctions.PERCENTAGE_UNIQUE]: aggPercentageUnqiue,
  [AggregateFunctions.SUM]: aggSum,
  [AggregateFunctions.AVERAGE]: aggAverage,
  [AggregateFunctions.MEDIAN]: aggMedian,
  [AggregateFunctions.MAX]: aggMax,
  [AggregateFunctions.MIN]: aggMin,
  [AggregateFunctions.RANGE]: aggRange,
  [AggregateFunctions.STANDARD_DEVIATION]: aggStandardDeviation,
  [AggregateFunctions.CHECKED]: aggChecked,
  [AggregateFunctions.UNCHECKED]: aggUnchecked,
  [AggregateFunctions.PERCENTAGE_CHECKED]: aggCheckedPercentage,
  [AggregateFunctions.PERCENTAGE_UNCHECKED]: aggUncheckedPercentage,
  [AggregateFunctions.EARLIEST_DATE]: aggEarliestDate,
  [AggregateFunctions.LATEST_DATE]: aggLatestDate,
  [AggregateFunctions.DATE_RANGE_DAYS]: aggDateRangeDays,
  [AggregateFunctions.DATE_RANGE_MONTH]: aggDateRangeMonths,
};

export const getSummaryValue = (values: any[], aggType: AggregateFunctions): number | string => {
  const func = GET_SUMMARY_VALUE_FUNC[aggType];
  if (!func) return "";
  return func(values);
};
